// Copyright (c) 2025, Quantum Xchange, Inc.
'use strict'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import { useContext, useRef, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'

import { UiContext } from 'js/contexts/uiContext'

import { useChangePasswordMutation } from '../../queries/userQueries'

const ChangePasswordModal = ({ onClose, isCancelable = true, passwordExpired = false }) => {
    const [ui] = useContext(UiContext)

    const [currentPasswordError, setCurrentPasswordError] = useState(null)
    const [newPasswordError, setNewPasswordError] = useState(null)
    const [confirmNewPasswordError, setConfirmNewPasswordError] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)

    const currentPasswordInput = useRef()
    const confirmNewPasswordInput = useRef()
    const newPasswordInput = useRef()

    const theme = useTheme()
    const { t } = useTranslation()

    const { mutate: changePasswordMutate, isPending: isChangingPassword } = useChangePasswordMutation()

    const confirm = async () => {
        try {
            const currentPassword = currentPasswordInput.current.value.trim()
            const newPassword = newPasswordInput.current.value.trim()
            const confirmNewPassword = confirmNewPasswordInput.current.value.trim()
            let valid = true

            setCurrentPasswordError(null)
            setNewPasswordError(null)
            setConfirmNewPasswordError(null)
            setErrorMessage(null)

            if (currentPassword.length === 0) {
                setCurrentPasswordError('Required')
                valid = false
            }

            if (newPassword.length === 0) {
                setNewPasswordError('Required')
                valid = false
            }

            if (confirmNewPassword.length === 0) {
                setConfirmNewPasswordError('Required')
                valid = false
            }

            if (newPassword !== confirmNewPassword) {
                setConfirmNewPasswordError(t('change_password_modal.confirm_password_error_label'))
                valid = false
            }

            if (!valid) return

            changePasswordMutate(
                {
                    uid: ui.user?.uid,
                    username: ui.user?.username,
                    currentPassword,
                    newPassword,
                    axiosConfig: { noAuthFailure: true },
                },
                {
                    onSuccess: () => submit(newPassword),
                    onError: err => {
                        if (err.response?.status) {
                            switch (err.response?.status) {
                                case 400:
                                    var newPasswordValidation = err.response?.data?.find(validation => validation.path === 'newPassword')
                                    if (newPasswordValidation) {
                                        if (newPasswordValidation.msg.min && newPasswordValidation.msg.max) {
                                            setNewPasswordError(
                                                t('change_password_modal.invalid_password_min_and_max_length_label', {
                                                    minLength: newPasswordValidation.msg.min,
                                                    maxLength: newPasswordValidation.msg.max,
                                                }),
                                            )
                                        } else if (newPasswordValidation.msg.min) {
                                            setNewPasswordError(
                                                t('change_password_modal.invalid_password_min_length_label', {
                                                    minLength: newPasswordValidation.msg.min,
                                                }),
                                            )
                                        } else if (newPasswordValidation.msg.max) {
                                            setNewPasswordError(
                                                t('change_password_modal.invalid_password_max_length_label', {
                                                    maxLength: newPasswordValidation.msg.max,
                                                }),
                                            )
                                        } else {
                                            setNewPasswordError(t('change_password_modal.invalid_password_length_label'))
                                        }
                                    } else setErrorMessage(t('change_password_modal.invalid_request_label'))
                                    break
                                case 401:
                                    setCurrentPasswordError(t('change_password_modal.current_password_incorrect_label'))
                                    break
                                case 404:
                                    setErrorMessage(t('change_password_modal.user_does_not_exist_label'))
                                    break
                                case 409:
                                    setNewPasswordError(t('change_password_modal.password_already_taken_label'))
                                    break
                                default:
                                    setErrorMessage(t('change_password_modal.password_change_failed_label'))
                                    break
                            }
                        } else {
                            setErrorMessage(t('common.unexpected_error_label'))
                        }
                    },
                },
            )
        } catch (err) {
            setErrorMessage(t('common.unexpected_error_label'))
        }
    }

    const cancel = () => {
        setCurrentPasswordError(null)
        setNewPasswordError(null)
        setConfirmNewPasswordError(null)
        setErrorMessage(null)

        if (onClose) onClose()
    }

    const submit = newPassword => {
        setCurrentPasswordError(null)
        setNewPasswordError(null)
        setConfirmNewPasswordError(null)
        setErrorMessage(null)

        if (onClose) onClose(newPassword)
    }

    return (
        <Modal show onHide={cancel} keyboard={isCancelable} backdrop={isCancelable ? true : 'static'}>
            <Form
                noValidate
                onSubmit={e => {
                    e.preventDefault()
                    confirm()
                }}
            >
                <Modal.Header closeButton={isCancelable} closeVariant={theme.colors.modal.closeVariant}>
                    <Modal.Title>{t('change_password_modal.change_password_title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {passwordExpired && (
                        <div className="mb-3 text-warning">
                            <FontAwesomeIcon icon="fa-exclamation-triangle" className="me-2" />
                            {t('change_password_modal.current_password_expired_label')}
                        </div>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="currentPassword">{t('change_password_modal.current_password_label')}</Form.Label>
                        <Form.Control
                            id="currentPassword"
                            disabled={isChangingPassword}
                            type="password"
                            autoComplete="off"
                            autoFocus={true}
                            ref={currentPasswordInput}
                            isInvalid={currentPasswordError}
                        />
                        <Form.Control.Feedback type="invalid">{currentPasswordError}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="newPassword">{t('change_password_modal.new_password_label')}</Form.Label>
                        <Form.Control
                            id="newPassword"
                            disabled={isChangingPassword}
                            type="password"
                            autoComplete="off"
                            ref={newPasswordInput}
                            isInvalid={newPasswordError}
                        />
                        <Form.Control.Feedback type="invalid">{newPasswordError}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="confirmNewPassword">{t('change_password_modal.confirm_password_label')}</Form.Label>
                        <Form.Control
                            id="confirmNewPassword"
                            disabled={isChangingPassword}
                            type="password"
                            autoComplete="off"
                            ref={confirmNewPasswordInput}
                            isInvalid={confirmNewPasswordError}
                        />
                        <Form.Control.Feedback type="invalid">{confirmNewPasswordError}</Form.Control.Feedback>
                    </Form.Group>
                    {errorMessage && <div className="text-danger mt-3">{errorMessage}</div>}
                </Modal.Body>
                <Modal.Footer>
                    {isCancelable && (
                        <Button className="me-2" variant="outline-secondary" disabled={isChangingPassword} onClick={cancel}>
                            {t('common.cancel_button')}
                        </Button>
                    )}
                    <Button type="submit" disabled={isChangingPassword}>
                        {isChangingPassword ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Saving
                            </>
                        ) : (
                            <>Save</>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

ChangePasswordModal.propTypes = {
    isCancelable: PropTypes.bool,
    onClose: PropTypes.func,
    passwordExpired: PropTypes.bool,
}

export default ChangePasswordModal
